import React, { useState, useEffect, useRef, useContext } from 'react';
import './FacilityDetailModal.css';
import Modal from '../Modal/Modal';
import WebLinkList from '../ProcessChanges/WebLinkList'
import OrganizationHistory from '../OrganizationHistory/OrganizationHistory';
import FacilityLogo from '../FacilityLogo/FacilityLogo';
import TruncateText from '../TruncateText/TruncateText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ActionButtons from '../ActionButtons/ActionButtons';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import convenienceApi from '../../common/api/convenience';
import logComponent from '../../common/logComponent';

export default function FacilityDetailModal({ facility, changeOrganization, updateFacility, hide }) {
  const environmentContext = useContext(EnvironmentContext);
  const { activeUser: user } = environmentContext;
  const [logoToUpload, setLogoToUpload] = useState(null);
  const [status, setStatus] = useState('initial');
  const [modalShouldExit, setModalShouldExit] = useState(false);
  const [weblinks, setWeblinks] = useState(null);
  const [weblinksLoading, setWeblinksLoading] = useState(null);
  const [weblinksError, setWeblinksError] = useState(null);
  const [logoErrorResponse, setLogoErrorResponse] = useState(null);
  const modalRef = useRef(null);
  const actionButtons = useRef(null);


  if (logComponent('FacilityDetailModal')) { console.debug('FacilityDetailModal rendered'); }

  const setModalRef = (el) => {
    modalRef.current = el;
  }

  useEffect(() => {
    if (logComponent('FacilityDetailModal')) { console.debug('FacilityDetailModal onMount'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const { hasToken } = environmentContext;
    if (!hasToken) return;
  }, []);

  useEffect(() => {
    if (logComponent('FacilityDetailModal')) { console.debug('FacilityDetailModal useEffect [environmentContext.hasToken]'); }
    const { hasToken } = environmentContext;
    if (!hasToken) return;
    loadWeblinks();

  }, [environmentContext.hasToken]);



  const loadWeblinks = () => {
    if (logComponent('FacilityDetailModal')) { console.debug('FacilityDetailModal loadWeblinks'); }
    const { hasToken } = environmentContext;
    if (!hasToken) return;

    setWeblinks(null);
    setWeblinksLoading(true);
    setWeblinksError(false);

    convenienceApi.getWeblinksForOrganization(facility)
      .then(
        result => {
          setWeblinks(result);
          setWeblinksLoading(false);
          setWeblinksError(false);
        },
        error => {
          setWeblinks(null);
          setWeblinksLoading(false);
          setWeblinksError(true);
        });
  }

  const handleCorporationClick = (e) => {
    if (logComponent('FacilityDetailModal')) { console.debug('FacilityDetailModal handleCorporationClick'); }
    const { corporationId } = e.currentTarget.dataset;

    // get facility based on id
    // NOTE: we want == in this case b/c the facilityId coming from the
    // dataset is a string, but the value in the linkedFacility is a number
    const targetCorporation = facility.uiData.linkedCorporations.find(corporation =>
      corporation.salesforceAccountId &&
      corporation.salesforceAccountId == corporationId); // eslint-disable-line

    changeOrganization(targetCorporation);
  }

  const setNewLogo = (logo) => {
    if (logComponent('FacilityDetailModal')) { console.debug('FacilityDetailModal setNewLogo'); }
    setLogoToUpload(logo);
    setStatus('initial');
  }

  const getServiceIcon = (isUsingService) => {
    if (logComponent('FacilityDetailModal')) { console.debug('FacilityDetailModal getServiceIcon'); }
    if (isUsingService) {
      return <FontAwesomeIcon icon="check-circle" className="Facility-Detail-Modal__active-service" />;
    } else {
      return <FontAwesomeIcon icon="times-circle" className="Facility-Detail-Modal__inactive-service" />;
    }
  }

  const submit = () => {
    if (logComponent('FacilityDetailModal')) { console.debug('FacilityDetailModal submit'); }

    if (!logoToUpload) {
      startModalExit();
    } else {
      setStatus('pending');

      convenienceApi.uploadLogo(facility, logoToUpload)
        .then(
          result => {
            setStatus('success');
            updateFacility(result);
            startModalExit();
          },
          error => {
            setLogoErrorResponse(error.response.data);
            setStatus('failure');
            modalRef.current.scrollTop = 0;
          }
        )
    }
  }

  const startModalExit = () => {
    if (logComponent('FacilityDetailModal')) { console.debug('FacilityDetailModal startModalExit'); }
    setModalShouldExit(true);
  }


  return (
    <Modal isVisible={true} shouldExit={modalShouldExit} hide={hide} ref={setModalRef} remainOnBackdropClick={true}>
      <div className="Facility-Detail-Modal">
        <h4 className="Facility-Detail-Modal__heading">{facility.name}</h4>
        <p className={"Facility-Detail-Modal__page-error" + (status === 'failure' ? ' visible' : '')} >{logoErrorResponse ? logoErrorResponse : 'Sorry, something went wrong.  Changes were not saved.  Please try again.'}</p>
        <dl className="Facility-Detail-Modal__list">
          {
            user.isAdmin &&
            <React.Fragment>
              <dt>Facility Logo</dt>
              <dd>
                <FacilityLogo
                  facility={facility}
                  onChange={setNewLogo} />
              </dd>
            </React.Fragment>
          }
          <dt>Status</dt>
          <dd>Active {/* TODO: Replace with dynamic data once it's available */}</dd>
          <dt>About Us</dt>
          <dd>
            {!facility.aboutUs ?
              <span className="Facility-Detail-Modal__empty-field">Not Provided</span> :
              <TruncateText text={facility.aboutUs} truncateAt={100} />
            }
          </dd>
          <dt>Postal Code</dt>
          <dd>
            {facility.postalCode ||
              <span className="Facility-Detail-Modal__empty-field">Not Provided</span>
            }
          </dd>
          <dt>Industry Type</dt>
          <dd>
            {facility.industryType ||
              <span className="Facility-Detail-Modal__empty-field">Not Provided</span>
            }
          </dd>
          <dt>Additional Services</dt>
          <dd className="Facility-Detail-Modal__additional-services">
            <span className="Facility-Detail-Modal__additional-service">{getServiceIcon(facility.usingCatie)}CATIE</span>
            <span className="Facility-Detail-Modal__additional-service">{getServiceIcon(facility.usingSara)}SARA</span>
            <span className="Facility-Detail-Modal__additional-service">{getServiceIcon(facility.usingEMessenger)}eMessenger</span>
          </dd>
          <dt>Salesforce Site ID</dt>
          <dd>{facility.salesforceSiteId}</dd>
          <dt>Web Links</dt>
          <dd>
            {
              weblinksLoading ?
                <p className="Facility-Detail-Modal__weblinks-loading">Loading web links...</p> :
                weblinksError ?
                  <p className="Facility-Detail-Modal__weblinks-error">Failed to load web links.
                    <button
                      className="Facility-Detail-Modal__reload-weblinks"
                      onClick={loadWeblinks}>
                      Try Again
                      <FontAwesomeIcon icon="redo-alt" />
                    </button>
                  </p> :
                  (!weblinks || weblinks.length === 0) ?
                    <span className="Facility-Detail-Modal__empty-field">Not Provided</span> :
                    <WebLinkList links={weblinks} />
            }
          </dd>
          <dt>Corporate Relationships</dt>
          <dd>
            {
              facility.uiData.linkedCorporations.length === 0 ?
                <span className="Facility-Detail-Modal__empty-field">No Corporate Relationships</span> :
                <ul className="Facility-Detail-Modal__linked-corporations">
                  {
                    facility.uiData.linkedCorporations.map(corporation =>
                      <li
                        className="Facility-Detail-Modal__corporation-item"
                        key={corporation.salesforceAccountId}>
                        <span className="Facility-Detail-Modal__corporation-id">{corporation.salesforceAccountId}</span>
                        <span className="Facility-Detail-Modal__corporation-name">
                          {corporation.name}
                          {
                            (!corporation.status || corporation.status !== 'rejected') && user.isAdmin &&
                            <button
                              className="Facility-Detail-Modal__corporation-link"
                              data-corporation-id={corporation.salesforceAccountId}
                              onClick={handleCorporationClick}>
                              <FontAwesomeIcon icon="external-link-alt" />
                            </button>
                          }
                        </span>
                      </li>
                    )
                  }
                </ul>
            }
          </dd>
          <dt>History</dt>
          <dd>
            <OrganizationHistory organization={facility} />
          </dd>
        </dl>
        {
          user.isAdmin ?
            <ActionButtons
              ref={actionButtons}
              mainAction={submit}
              mainActionText="Save changes"
              cancel={startModalExit}
              cancelText="Cancel"
              currentStatus={status} /> :
            <ActionButtons
              ref={actionButtons}
              cancel={startModalExit}
              cancelText="Close" />
        }
      </div>
    </Modal>
  );
}